import { useMemo } from 'react';

import { Box } from 'approot/shared/design-system/box/box.style';
import { BadgeSmallStyled } from 'approot/shared/badge/badge.style';
import { ComponentTable } from 'approot/activity-builder/components/component.constants';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import {
  ClassroomAnswerInstruction,
  ClassroomSuggestedAnswer,
} from '../classroom-answer.style';
import TableReadonlyAnswers from 'approot/activity-builder/components/table/table-readonly-answers';
import {
  ACTIVITIES_BASE_HEIGHT_SMALL,
  SUGGESTED_ANSWER_EXAMPLE,
  SUGGESTED_ANSWER_VARY,
} from 'global/constants';
import { getAnswerTypeLabel } from 'approot/activity-builder/components/suggested-answers/logic/get-answer-type-label';
import { ResponseFieldReadonly } from 'approot/activity-builder/components/response/response-field-readonly.style';
import {
  getRichtextValue,
  isRichtextValueEmpty,
} from 'approot/activity-builder/components/shared/logic/get-richtext-value';
import { getRichtextAnswer } from 'approot/activity-builder/components/shared/logic/get-richtext-answer';
import { SUGGESTED_ANSWER_BASE_RICHTEXT } from 'approot/activity-builder/components/suggested-answers/suggested-answer.constants';
import { useGetRichtextAnswer } from 'approot/activity-builder/components/suggested-answers/suggested-answer.hooks';
import { TableWrapper } from 'approot/activity-builder/components/table/table-check-answer.style';

type Props = {
  component: Omit<ComponentTable, '__typename'>;
  width: number;
};

const TableResponseSuggestedAnswers = ({ component, width }: Props) => {
  const { suggestedAnswerProperties } = component;

  const answerType =
    suggestedAnswerProperties?.suggestedAnswerType || SUGGESTED_ANSWER_EXAMPLE;
  const answers = suggestedAnswerProperties?.suggestedAnswers || [];

  const isAnswersTableEmpty = useMemo(
    () =>
      answerType !== SUGGESTED_ANSWER_VARY &&
      suggestedAnswerProperties?.suggestedAnswers
        ?.filter(answer => answer.answerReferences !== null)
        .every(answer => isRichtextValueEmpty(getRichtextAnswer(answer))),
    [answerType, suggestedAnswerProperties]
  );

  const answer = useGetRichtextAnswer({
    componentId: component.id,
    suggestedAnswerProperties,
  });

  const answerTypeBadge = (
    <BadgeSmallStyled>{getAnswerTypeLabel(answerType)}</BadgeSmallStyled>
  );

  return (
    <ClassroomAnswerInstruction>
      <SubActivityView subActivityDetails={component.subActivityProperties}>
        <TableWrapper $maxWidth={width}>
          {answerType === SUGGESTED_ANSWER_VARY ? (
            <ClassroomSuggestedAnswer>
              <ResponseFieldReadonly
                $numberOflines={3}
                $baseHeight={ACTIVITIES_BASE_HEIGHT_SMALL}
              />
              {answerTypeBadge}
            </ClassroomSuggestedAnswer>
          ) : (
            <>
              {!isAnswersTableEmpty && (
                <ClassroomSuggestedAnswer>
                  <TableReadonlyAnswers
                    {...component}
                    answers={answers}
                    isSuggestedAnswer
                  />
                  {answerTypeBadge}
                </ClassroomSuggestedAnswer>
              )}

              {answerType === SUGGESTED_ANSWER_EXAMPLE &&
                !isRichtextValueEmpty(answer) && (
                  <Box $marginTop={24}>
                    <ClassroomSuggestedAnswer>
                      <ResponseFieldReadonly
                        $numberOflines={1}
                        $baseHeight={ACTIVITIES_BASE_HEIGHT_SMALL}
                        dangerouslySetInnerHTML={{
                          __html: getRichtextValue(
                            answer,
                            SUGGESTED_ANSWER_BASE_RICHTEXT
                          ),
                        }}
                      />
                      {answerTypeBadge}
                    </ClassroomSuggestedAnswer>
                  </Box>
                )}
            </>
          )}
        </TableWrapper>
      </SubActivityView>
    </ClassroomAnswerInstruction>
  );
};

export default TableResponseSuggestedAnswers;
